import { Component, Input, OnChanges } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ErrorActions } from 'src/app/error/store/error.slice';

import { TourismServiceViewModel } from '@app/api/models';

import { getServerErrorForField } from '../../helpers/form-helper';
import { Language } from '../../interfaces/language';
import { LanguageCodesService } from '../../services/language-codes.service';
import { notBlank } from '../../validators/not-blank.validator';

@Component({
  selector: 'app-localized-tourism-service-subform',
  templateUrl: './localized-tourism-service-subform.component.html',
  styleUrls: ['./localized-tourism-service-subform.component.scss'],
})
export class LocalizedTourismServiceSubformComponent implements OnChanges {
  static DEFAULT_LANGUAGE = 'hun';
  static VALIDATORS = [Validators.required, notBlank];
  static EMPTY_TOURISM_SERVICE: TourismServiceViewModel = {
    title: '',
    description: '',
    tourismServicePictureId: '',
  };

  @Input() formGroupAbstract!: AbstractControl;

  @Input() title?: string;
  @Input() descriptionAutosizeMinRows: number = 3;

  form!: FormGroup;

  languageSelect: UntypedFormControl = new UntypedFormControl(
    null,
    Validators.required
  );

  constructor(
    private languageCodesService: LanguageCodesService,
    private store: Store,
    private translateService: TranslateService
  ) {}

  static buildDefaultForm(): FormGroup {
    return this.buildForm({
      [this.DEFAULT_LANGUAGE]: this.EMPTY_TOURISM_SERVICE,
    });
  }

  static buildForm(title: {
    [key: string]: TourismServiceViewModel;
  }): FormGroup {
    return new FormGroup(
      Object.fromEntries(
        Object.entries(title)
          .filter(([language, _]) => language === this.DEFAULT_LANGUAGE)
          .map(([language, service]) => [
            language,
            this._buildLocalizedServiceGroup(service),
          ])
      )
    );
  }

  static _buildLocalizedServiceGroup(
    service?: TourismServiceViewModel
  ): FormGroup {
    service = service || this.EMPTY_TOURISM_SERVICE;
    return new UntypedFormGroup({
      title: new UntypedFormControl(service.title, this.VALIDATORS),
      description: new UntypedFormControl(service.description, this.VALIDATORS),
      tourismServicePictureId: new UntypedFormControl(
        service.tourismServicePictureId
      ),
    });
  }

  ngOnChanges(): void {
    if (!this.formGroupAbstract) {
      throw Error('Attribute "formGroupAbstract" is required');
    }
    this.form = this.formGroupAbstract as FormGroup;
  }

  onAdd() {
    const selectedLanguageCode: string | null =
      this.languageSelect?.value?.code;
    if (selectedLanguageCode) {
      if (this.form.contains(selectedLanguageCode)) {
        this.store.dispatch(
          ErrorActions.handleError({
            traceId: 0,
            message: this.translateService.instant(
              'SHARED.LOCALIZED_VALUE_SUBFORM.ALREADY_ADDED'
            ),
            errorCode: '',
            globalErrors: {},
            fieldErrors: {},
          })
        );
      } else {
        this.form.addControl(
          selectedLanguageCode,
          LocalizedTourismServiceSubformComponent._buildLocalizedServiceGroup()
        );
        this.languageSelect.reset();
      }
    } else {
      this.languageSelect.markAsTouched();
      window.setTimeout(() => {
        // keeps control from staying red forever
        this.languageSelect.markAsUntouched();
      }, 3000);
    }
  }

  onDelete(selectedLanguageCode: string) {
    this.form.removeControl(selectedLanguageCode);
    this.form.markAsDirty();
  }

  getLangCodes(): string[] {
    if (!this.form) return [];
    if (!this.form.controls) return [];
    return Object.keys(this.form.controls);
  }

  getNameByCode(code: string): string {
    return this.languageCodesService.getNameByCode(code);
  }

  getLanguages(): Language[] {
    return this.languageCodesService.availableLanguages;
  }

  nameFunction(item: Language): string {
    return item.name;
  }

  equalsFunction(a: Language, b: Language): boolean {
    return a && b && a.code === b.code;
  }

  matchFunction(item: Language, searchPattern: string): boolean {
    const fuzzyMatch = (_item: string, _searchPattern: string): boolean => {
      _searchPattern = _searchPattern.toLowerCase();
      _item = _item.toLowerCase();
      _searchPattern = '.*' + _searchPattern.split('').join('.*') + '.*';
      const re = new RegExp(_searchPattern);
      return re.test(_item);
    };

    return (
      fuzzyMatch(item.code, searchPattern) ||
      fuzzyMatch(item.name, searchPattern)
    );
  }

  getServerError(fieldName: string): any {
    return getServerErrorForField(this.form, fieldName);
  }
}
